import React, { useState, useEffect } from 'react';
import {
   Grid,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Button,
   FormControlLabel,
   Switch,
   Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Loading from '../../Components/Loading';
import StyledDashboardLayout from '../style';
import {
   useGetDiscountByIdQuery,
   useUpdateDiscountMutation,
} from '../../Redux/Service/DiscountApi';

const DiscountEdit = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [initialValues, setInitialValues] = useState({
      name: '',
      type: 'percentage',
      value: '',
      description: '',
      is_active: true,
   });
   const { data: discountData, isLoading: isFetching } = useGetDiscountByIdQuery(id);
   const [updateDiscount, { isLoading: isMutating }] = useUpdateDiscountMutation();

   const validationSchema = Yup.object().shape({
      name: Yup.string().required('Name is required').max(50, "Name should be atmost 50 characters"),
      type: Yup.string().oneOf(['percentage', 'fixed']).required('Type is required'),
      value: Yup.number()
         .typeError('Value must be a number')
         .required('Value is required')
         .test('value-range', function (value) {
            const { type } = this.parent;
            if (type === 'percentage') {
               if (value < 0 || value > 100) {
                  return this.createError({
                     message: 'Percentage must be between 0 and 100',
                  });
               }
            } else if (type === 'fixed') {
               if (value < 0) {
                  return this.createError({
                     message: 'Amount cannot be negative',
                  });
               }

               if (value > 99999) {
                  return this.createError({
                     message: 'Amount cannot be more than 99999',
                  });
               }
            }
            return true;
         }),
      description: Yup.string().optional().max(250, "Description should be atmost 250 characters"),
      is_active: Yup.boolean(),
   });

   useEffect(() => {
      if (discountData?.response) {
         const discount = discountData.response;
         setInitialValues({
            name: discount?.name || '',
            type: discount?.type || 'percentage',
            value: discount?.value ?? '',
            description: discount?.description || '',
            is_active: typeof discount?.is_active === 'boolean' ? discount.is_active : true,
         });
      }
   }, [discountData]);

   const handleSubmit = async (values, { setSubmitting }) => {
      try {
         const numericValue = Number(values.value);

         // Defensive check: validate range based on type
         if (values.type === 'percentage' && (numericValue < 0 || numericValue > 100)) {
            toast.error('Percentage must be between 0 and 100');
            setSubmitting(false);
            return;
         }

         if (values.type === 'fixed' && numericValue < 0) {
            toast.error('Amount cannot be negative');
            setSubmitting(false);
            return;
         }

         const payload = { id, ...values, value: numericValue };

         const result = await updateDiscount(payload).unwrap();
         toast.success(result?.message || 'Discount updated successfully');
         navigate('/discount');
      } catch (error) {
         console.error('Update discount error', error);
         const message = error?.data?.message || 'Failed to update discount';
         toast.error(message);
      } finally {
         setSubmitting(false);
      }
   };

   if (isFetching) return <Loading />;

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Edit Discount</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/discount" className="btn addBtn">
                           {' '}
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <Box sx={{ p: 3 }}>
               <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
                  <Box sx={{ mb: 2 }}>
                     <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                        Update Discount
                     </Typography>
                     <Typography variant="body2" color="text.secondary">
                        Update discount details below. Choose type as percentage or fixed amount.
                     </Typography>
                  </Box>

                  <Formik
                     initialValues={initialValues}
                     enableReinitialize
                     validationSchema={validationSchema}
                     onSubmit={handleSubmit}
                  >
                     {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                        <Form>
                           <Grid container spacing={3} alignItems="center">
                              <Grid item xs={12} md={6}>
                                 <Field
                                    name="name"
                                    as={TextField}
                                    label="Discount Name"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                 />
                              </Grid>

                              <Grid item xs={6} md={3}>
                                 <FormControl fullWidth size="small">
                                    <InputLabel id="discount-type-label">Type</InputLabel>
                                    <Field
                                       name="type"
                                       as={Select}
                                       labelId="discount-type-label"
                                       label="Type"
                                       value={values.type}
                                       onChange={(e) => setFieldValue('type', e.target.value)}
                                    >
                                       <MenuItem value="percentage">Percentage</MenuItem>
                                       <MenuItem value="fixed">Fixed</MenuItem>
                                    </Field>
                                    <ErrorMessage
                                       name="type"
                                       component="div"
                                       style={{
                                          color: 'red',
                                          marginTop: '6px',
                                       }}
                                    />
                                 </FormControl>
                              </Grid>

                              <Grid item xs={6} md={3}>
                                 <Field
                                    name="value"
                                    as={TextField}
                                    label={
                                       values.type === 'percentage'
                                          ? 'Percentage (%)'
                                          : 'Fixed Amount'
                                    }
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    type="number"
                                    inputProps={
                                       values.type === 'percentage'
                                          ? { min: 0, max: 100, step: 'any' }
                                          : { min: 0, step: 'any' }
                                    }
                                    error={touched.value && Boolean(errors.value)}
                                    helperText={touched.value && errors.value}
                                 />
                              </Grid>

                              <Grid item xs={12}>
                                 <Field
                                    name="description"
                                    as={TextField}
                                    label="Description"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    error={touched.description && Boolean(errors.description)}
                                    helperText={touched.description && errors.description}
                                 />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                 <FormControlLabel
                                    control={
                                       <Field
                                          as={Switch}
                                          name="is_active"
                                          checked={values.is_active}
                                          onChange={(e) =>
                                             setFieldValue('is_active', e.target.checked)
                                          }
                                          color="primary"
                                       />
                                    }
                                    label={values.is_active ? 'Active' : 'Inactive'}
                                 />
                              </Grid>

                              <Grid
                                 item
                                 xs={12}
                                 md={6}
                                 sx={{ textAlign: { xs: 'left', md: 'right' } }}
                              >
                                 <Button
                                    component={Link}
                                    to="/discount"
                                    variant="outlined"
                                    sx={{ mr: 2 }}
                                 >
                                    Cancel
                                 </Button>

                                 <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={isSubmitting}
                                 >
                                    {isSubmitting ? 'Updating...' : 'Update Discount'}
                                 </Button>
                              </Grid>
                           </Grid>
                        </Form>
                     )}
                  </Formik>
               </Paper>
            </Box>
         </div>
      </StyledDashboardLayout>
   );
};

export default DiscountEdit;
